import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { THIS_EXPR } from '@angular/compiler/src/output/output_ast';

@Component({
  selector: 'app-day-hour-input',
  templateUrl: './day-hour-input.component.html',
  styleUrls: ['./day-hour-input.component.scss']
})
export class DayHourInputComponent implements OnInit {
  @Input() control: FormControl;
  @Output() inputChange = new EventEmitter<void>();

  public inputs: FormControl[];
  public value: string;
  public valid: boolean;
  public lastRemoved: string;
  public currentMinute: string;

  constructor() { }

  constrainValue(hourMinuteZone: string) {
    if (!this.currentMinute) {
      return hourMinuteZone;
    }
    if (hourMinuteZone.length !== '00:00 XXX'.length) {
      return hourMinuteZone;
    }
    const hour: string = hourMinuteZone.split(' ')[0].split(':')[0];
    const minute: string = hourMinuteZone.split(' ')[0].split(':')[1];
    const zone: string = hourMinuteZone.split(' ')[1];
    if (isNaN(parseInt(hour, 10)) || isNaN(parseInt(minute, 10))) {
      return hourMinuteZone;
    }
    if (minute !== this.currentMinute) {
      return hour + ':' + this.currentMinute + ' ' + zone;
    }
    return hourMinuteZone;
  }

  ngOnInit() {
    const startValue = this.control && this.control.value ? this.control.value.toString() : '';

    if (startValue && startValue.length > 3 && !isNaN(parseInt(startValue.substr(3, 2), 10))) {
      this.currentMinute = startValue.substr(3, 2);
    } else {
      this.currentMinute = '00';
    }

    this.inputs = startValue.split(',').map(value => new FormControl(this.constrainValue(value), [Validators.required]));
  }

  // Makes sure every hour has the same minute due to backend constraints
  normalizeValue(value: string): string {
    if (!value.includes(',')) {
      return value;
    }
    const inputs = value.split(',').map(input => input.trim());
    const list = inputs.map(a => a.split(' ')[0].split(':')[1]);
    const minute = list[0] && !isNaN(parseInt(list[0], 10)) ? parseInt(list[0], 10) : 0;
    const newInputs = inputs.map(input => ({
      time: input.split(' ')[0].split(':')[0].toString().padStart(2, '0') + minute.toString().padStart(2, '0'),
      zone: input.split(' ')[1]
    }));
    return newInputs.map(input => input.time + ' ' + input.zone).join(',');
  }

  onChange() {
    const times = this.inputs.map(
      input => input.value === 'BRT' || input.value === 'UTC' ? '00:00 ' + input.value : input.value
    ).map(dayHourString => ({
      hour: dayHourString.split(' ')[0].split(':')[0] as string,
      minute: dayHourString.split(' ')[0].split(':')[1] as string,
      zone: dayHourString.split(' ')[1] as string
    }));

    if (this.currentMinute) {
      if (times[0].minute !== this.currentMinute) {
        this.currentMinute = times[0].minute;
        this.inputs.forEach((input, i) => {
          if (i === 0) {
            return;
          }
          const newValue = this.constrainValue(input.value);
          if (newValue === input.value) {
            return;
          }
          times[i].minute = this.currentMinute;
          input.setValue(times[i].hour + ':' + times[i].minute + ' ' + times[i].zone);
        });
        // Force it to change visually
        document.querySelectorAll(
          'app-day-hour-input app-hour-minute-input input.minute'
        ).forEach(
          (input: HTMLInputElement) => input.value = times[0].minute
        );
      }
    }

    this.currentMinute = times[0].minute;

    const value = this.inputs.map(input => input.value).join(',');
    let hasChanged = false;
    if (this.value !== value) {
      hasChanged = true;
      this.value = value;
      if (this.control) {
        this.control.setValue(value);
      }
    }
    const isValid = this.inputs.every(input => input.valid);
    if (this.valid !== isValid) {
      this.valid = isValid;
      hasChanged = true;
      if (this.control) {
        this.control.setErrors(isValid ? null : { required: true });
      }
    }
    if (hasChanged) {
      this.inputChange.emit();
    }
  }

  onRemoveClick(inputIndex: number) {
    if (inputIndex === 0 && this.inputs.length <= 1) {
      console.log('Skipped removal');
      return;
    }
    this.lastRemoved = this.inputs[inputIndex].valid ? this.inputs[inputIndex].value : '';
    this.inputs.splice(inputIndex, 1);
    this.onChange();
  }

  onAddClick() {
    let newValue = this.lastRemoved ? this.lastRemoved : (this.inputs[this.inputs.length - 1].value || '');
    if (newValue.length !== '00:00 UTC'.length) {
      newValue = this.inputs[0].value;
    }
    this.inputs.push(new FormControl(this.constrainValue(newValue)));
    this.lastRemoved = '';
    window.setTimeout(this.focusLastHourInput.bind(this), 100);
  }

  focusLastHourInput(selectEmpty = false) {
    const inputs: HTMLInputElement[] = Array.prototype.map.call(
      document.querySelectorAll('app-day-hour-input input'),
      (e: HTMLInputElement) => e
    );
    if (inputs.length > 3) {
      inputs[inputs.length - 2].focus();
    }
  }
}
